<script setup lang="ts">
/**
 * KeyboardShortcutsDialog
 *
 * Modaler Overlay-Dialog mit der vollständigen, gruppierten Übersicht aller Tastenkürzel für
 * Auswahl/Bearbeitung der Notenvorschau. Datenquelle: {@link SHORTCUT_GROUPS} aus
 * `keyboardShortcuts.ts` (Single Source). Muster (Transition + fixed Overlay + ESC + Backdrop-
 * Klick) analog `FileHistoryDetailDialog.vue`.
 *
 * Sichtbarkeit via benanntem `defineModel('isOpen')`. Das Öffnen (Button „?" / Taste „?")
 * liegt beim Eltern-`MusicXmlPreview`.
 */
import {onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {Icon} from '@iconify/vue';
import {SHORTCUT_GROUPS} from './keyboardShortcuts';
import {focusWithoutScroll} from '@/shared/focus';

const isOpen = defineModel<boolean>('isOpen', {default: false, type: Boolean});

const dialogRef = ref<HTMLElement | null>(null);

function close(): void {
  isOpen.value = false;
}

// ESC schließt zuverlässig – in der CAPTURE-Phase, damit `stopImmediatePropagation` die
// (bubble-phase) `onKeyStroke`-Escape-Handler der `ScoreEditToolbar` NICHT gleichzeitig feuern
// lässt (sonst würde parallel die Notenauswahl aufgehoben). Nur aktiv, solange offen.
function onKeydownCapture(e: KeyboardEvent): void {
  if (!isOpen.value || e.key !== 'Escape') return;
  e.preventDefault();
  e.stopImmediatePropagation();
  close();
}

onMounted(() => document.addEventListener('keydown', onKeydownCapture, {capture: true}));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydownCapture, {capture: true}));

// Fokus in den Dialog setzen, sobald er geöffnet wird (A11y).
watch(isOpen, async (open) => {
  if (!open) return;
  await Promise.resolve();
  focusWithoutScroll(dialogRef.value);
});
</script>

<template>
  <Transition
      enter-active-class="animate-fade animate-duration-200"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
  >
    <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        @click.self="close"
    >
      <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          aria-label="Tastenkürzel"
          tabindex="-1"
          class="flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl outline-none"
      >
        <!-- Kopf -->
        <header class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3">
          <h2 class="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900">
            <Icon icon="fa6-solid:keyboard" class="shrink-0 text-blue-500"/>
            <span class="truncate">Tastenkürzel</span>
          </h2>
          <button type="button" class="btn btn-ghost !px-2 !py-1.5" aria-label="Schließen" @click="close">
            <Icon icon="fa6-solid:xmark"/>
          </button>
        </header>

        <!-- Inhalt -->
        <div class="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <section v-for="group in SHORTCUT_GROUPS" :key="group.title">
            <h3 class="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              <Icon :icon="group.icon" class="text-sm text-gray-400"/>
              {{ group.title }}
            </h3>
            <dl class="space-y-1">
              <div
                  v-for="(item, i) in group.items"
                  :key="i"
                  class="flex items-center justify-between gap-4 border-b border-gray-50 py-1"
              >
                <dt class="min-w-0 text-sm text-gray-700">
                  {{ item.description }}
                  <span v-if="item.note" class="ml-1 text-xs text-gray-400">({{ item.note }})</span>
                </dt>
                <dd class="flex shrink-0 items-center gap-1.5">
                  <template v-for="(combo, ci) in item.combos" :key="ci">
                    <span v-if="ci > 0" class="text-xs text-gray-400">/</span>
                    <span class="inline-flex items-center gap-1">
                      <template v-for="(token, ti) in combo" :key="ti">
                        <span v-if="ti > 0" class="text-xs text-gray-400">+</span>
                        <kbd class="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700 shadow-sm">{{ token }}</kbd>
                      </template>
                    </span>
                  </template>
                </dd>
              </div>
            </dl>
          </section>
        </div>

        <!-- Fuß -->
        <footer class="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-2.5 text-xs text-gray-500">
          <span>Tipp: mit
            <kbd class="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-gray-700 shadow-sm">?</kbd>
            öffnen/schließen</span>
        </footer>
      </div>
    </div>
  </Transition>
</template>
